import type { CeederoApiRefusal } from "@/api/ceedero-api";

import { CeederoApiError } from "@/api/ceedero-api";

import type { TranslationKey } from "@/localization/hooks/use-translation";

/**
 * A refusal from the API, as the wording the user reads.
 *
 * Returns a `TranslationKey`, so a refusal the transport can produce but the
 * catalogs have no wording for is a compile error rather than a raw code on
 * screen.
 *
 * `unauthorized` maps to the same wording as `invalid_credentials` on purpose:
 * on a sign-in they mean the same thing to the person typing, and the API
 * deliberately does not distinguish a wrong username from a wrong password.
 */
export const resolveSignInRefusalKey = (refusal: CeederoApiRefusal): TranslationKey => {
  switch (refusal) {
    case "invalid_credentials":
    case "unauthorized": {
      return "account.signIn.error.invalidCredentials";
    }
    case "not_entitled": {
      return "account.signIn.error.notEntitled";
    }
    case "rate_limited": {
      return "account.signIn.error.rateLimited";
    }
    default: {
      return "account.signIn.error.unavailable";
    }
  }
};

/**
 * Any thrown value, as the wording the user reads — or nothing when there is no
 * error at all.
 *
 * Kept here rather than in the form so the mapping is one statement per case
 * instead of a chain of ternaries at a call site, and so it can be tested
 * without rendering anything. Anything that is not a `CeederoApiError` is an
 * outage: a bug in this application is not something to explain to a user in
 * terms of their account.
 */
export const resolveSignInErrorKey = (error: unknown): TranslationKey | undefined => {
  if (error instanceof CeederoApiError) {
    return resolveSignInRefusalKey(error.refusal);
  }
  if (error) {
    return "account.signIn.error.unavailable";
  }
  return undefined;
};

/**
 * A refusal of a credential change, as the wording the user reads.
 *
 * Separate from the sign-in mapping because one code means two different things
 * to the person reading it. On a sign-in, `invalid_credentials` means "that
 * pair is wrong" and deliberately does not say which half. On a change form the
 * account is already known — the only credential being offered is the current
 * password, re-asked precisely because a session is evidence that somebody
 * signed in once and not that the person typing now knows it — so the same code
 * means "that password is wrong", and saying so is not enumeration.
 */
export const resolveCredentialChangeRefusalKey = (refusal: CeederoApiRefusal): TranslationKey => {
  switch (refusal) {
    case "invalid_credentials": {
      return "account.error.currentPasswordIncorrect";
    }
    case "username_taken": {
      return "account.error.usernameTaken";
    }
    case "rate_limited": {
      return "account.error.rateLimited";
    }
    case "unauthorized": {
      // The session went away underneath the form. Saying "sign in again" is
      // the only useful thing here; the transport draws the sign-in page as
      // soon as anything else asks who the user is.
      return "account.error.signedOut";
    }
    default: {
      return "account.error.unavailable";
    }
  }
};

export const resolveCredentialChangeErrorKey = (error: unknown): TranslationKey | undefined => {
  if (error instanceof CeederoApiError) {
    return resolveCredentialChangeRefusalKey(error.refusal);
  }
  if (error) {
    return "account.error.unavailable";
  }
  return undefined;
};

/**
 * A refusal of an avatar upload, as the wording the user reads.
 *
 * Its own mapping because two of these codes appear nowhere else and both have
 * an obvious next step: a file of the wrong kind, and one too large. Flattened
 * into "something went wrong" they would leave the user re-trying the same
 * file, which will fail the same way every time.
 */
export const resolveAvatarErrorKey = (error: unknown): TranslationKey | undefined => {
  if (!(error instanceof CeederoApiError)) {
    return error ? "account.error.unavailable" : undefined;
  }
  switch (error.refusal) {
    case "invalid_avatar": {
      return "account.error.avatarType";
    }
    case "payload_too_large": {
      return "account.error.avatarTooLarge";
    }
    case "rate_limited": {
      return "account.error.rateLimited";
    }
    case "unauthorized": {
      return "account.error.signedOut";
    }
    default: {
      return "account.error.unavailable";
    }
  }
};

/**
 * A refusal of an account recovery, as the wording the user reads.
 *
 * `account_unclaimed` is the one with an obvious next step and so the one that
 * must not be flattened: the purchase is real and has no account, which means
 * there is no password to reset and one is created from the desktop instead.
 */
export const resolveRecoveryErrorKey = (error: unknown): TranslationKey | undefined => {
  if (!(error instanceof CeederoApiError)) {
    return error ? "account.error.unavailable" : undefined;
  }
  switch (error.refusal) {
    case "invalid_recovery_code": {
      return "account.recovery.error.invalidCode";
    }
    case "account_unclaimed": {
      return "account.recovery.error.noAccount";
    }
    case "rate_limited": {
      return "account.recovery.error.rateLimited";
    }
    default: {
      return "account.error.unavailable";
    }
  }
};
